import { useMemo, useCallback, useRef } from 'react';

import { getEditorInlineCommentOptions } from '@confluence/annotation-provider-inline-comments/entry-points/editor';
import {
	useAnnotationsDispatch,
	useAnnotationManager,
} from '@confluence/annotation-provider-store/entry-points/AnnotationsContext';
import { fg } from '@confluence/feature-gating';
import { useAddCommentPermissionCheck } from '@confluence/comments-hooks/entry-points/useAddCommentPermissionCheck';
import { useContentType } from '@confluence/page-context';

export const useEditorAnnotationProviderOptions = ({
	pageId,
	editorActions,
	triggerAnalyticsEvent,
	updateUnresolvedInlineComment,
}: {
	pageId: string;
	editorActions: any;
	triggerAnalyticsEvent: any;
	updateUnresolvedInlineComment: any;
}) => {
	const { setEditorCreateProps, setEditorViewProps } = useAnnotationsDispatch();
	const annotationManager = useAnnotationManager();
	const [contentType] = useContentType();

	const { canAddComments } = useAddCommentPermissionCheck(pageId);

	const canAddCommentsRef = useRef(canAddComments);
	canAddCommentsRef.current = canAddComments;

	const getCanAddComments = useCallback(
		() =>
			fg('confluence_frontend_disable_inline_comment_button') ? canAddCommentsRef.current : true,
		[],
	);

	return useMemo(
		() => ({
			inlineComment: getEditorInlineCommentOptions({
				pageId,
				editorActions,
				triggerAnalyticsEvent,
				updateUnresolvedInlineComment,
				setEditorCreateProps,
				setEditorViewProps,
				editorAnnotationProviderManagersEnabled: fg(
					'confluence_frontend_editor_annotation_manager',
				),
				getCanAddComments,
				contentType,
			}),
			annotationManager,
		}),
		[
			pageId,
			triggerAnalyticsEvent,
			editorActions,
			updateUnresolvedInlineComment,
			setEditorCreateProps,
			setEditorViewProps,
			annotationManager,
			contentType,
			getCanAddComments,
		],
	);
};
